import { TAvailablePlan } from "@/redux/slices/store/types";
import { TBillingInterval } from "@/redux/slices/user/types";
import moment from "moment";

export const isDomainItem = (item: any) => item.type?.name === "Domain Name";

export const getDomainFlags = (item: any) => {
  const isDomain = isDomainItem(item);
  const isDomainTransfer = isDomain && item.type?.action === "transfer";
  const isDomainBackorder = isDomain && item.type?.action === "backorder";

  return { isDomain, isDomainTransfer, isDomainBackorder };
};

export const formatMoney = (amountInCents: number, currency: string) =>
  `${currency}${(amountInCents / 100).toFixed(2)}`;

export const buildDomainDescriptionsAndTip = ({
  item,
  itemName,
  billing_interval,
  currency,
  isDomain,
  isDomainTransfer,
  isDomainBackorder,
}: {
  item: any;
  itemName: string;
  billing_interval: TBillingInterval;
  currency: string;
  isDomain: boolean;
  isDomainTransfer: boolean;
  isDomainBackorder: boolean;
}): { descriptions: string[]; tip: React.ReactNode } => {
  let descriptions = [];
  let tip: React.ReactNode = null;

  let billingIntervalText = billing_interval === "monthly" ? "month" : "year";

  const numberTranferText =
    billing_interval === "monthly"
      ? `${formatMoney(
          item.cost_recurring,
          currency
        )} per ${billingIntervalText} upon successful transfer`
      : `${formatMoney(item.cost_service, currency)} one-off transfer fee + ${formatMoney(item.cost_recurring, currency)} per year`;

  switch (item.type?.name) {
    case "VoIP Plan Change":
      break;

    case "Domain Name":
      if (isDomainBackorder) {
        tip =
          "Backorder Fee + Domain Registration Fee charged upon Backorder success.";
        descriptions.push(
          `Domain Name: ${itemName}`,
          `Estimated Registration Price: ${formatMoney(
            item.cost_recurring,
            currency
          )}`,
          `Backorder Service Fee: ${formatMoney(item.cost_service, currency)}`
        );
      } else if (isDomainTransfer) {
        descriptions.push(`Domain Name: ${itemName}`, "Term: 1 year");
      } else {
        descriptions.push(
          `Domain Name: ${itemName}`,
          `Renews: ${moment().add(item.period, "years").format("Do MMM YYYY")}`
        );
      }
      break;

    case "Number": {
      const price =
        item.cost_net > 0
          ? `${formatMoney(item.cost_net, currency)} / per ${
              billingIntervalText
            }`
          : `Included in your ${billing_interval} plan`;
      descriptions.push(`Number: ${itemName}`, `Price: ${price}`);
      break;
    }

    case "Number Transfer": {
      const priceText =
        item.cost_recurring > 0
          ? numberTranferText
          : `Included in your ${billing_interval} plan upon successful transfer`;

      tip = "One-off Number Transfer porting fee.";
      descriptions.push(`Number: ${itemName}`, `Price: ${priceText}`);
      break;
    }

    case "PaYG Call Credit":
      descriptions.push(`Price: one-off payment`);
      break;

    case "User":
    case "VoIP Addon":
      descriptions.push([]);
      break;

    default:
      descriptions.push(itemName);
  }

  return { descriptions, tip };
};

export const findPlanForItem = (
  item: any,
  plans: TAvailablePlan[]
): TAvailablePlan | undefined => {
  if (!item?.type?.name) return undefined;

  if (item.type.name === "User") {
    const planId = item.type.sub_type_id + 1;
    return plans.find((p) => p.id === planId);
  }

  const basketPlanName = item?.description?.split(" ")[2];
  return plans.find((p) => p.name === basketPlanName);
};

export const makeCheckPrice =
  (item: any, plans: TAvailablePlan[], currency: string) =>
  (type: TBillingInterval) => {
    const plan = findPlanForItem(item, plans);
    const price = plan?.costs?.[type] ?? 0;
    const divisor = type === "yearly" ? 100 * 12 : 100;

    return `${currency}${(price / divisor).toFixed(2)}`;
  };

export const makeGetAddOnPrice =
  (item: any, plans: TAvailablePlan[], currency: string) => () => {
    const plan = findPlanForItem(item, plans);

    const monthlyAddOnCost = plan?.available_addons?.[0]?.costs?.monthly ?? 0;
    const price = (monthlyAddOnCost / 100).toFixed(2);

    return `${currency}${price} per user / month`;
  };

export const formatDate = (date: any) => moment(date).format("Do MMM YYYY");

export const isVoipAddon = (c?: any) => c?.type?.name === "VoIP Addon";

export const hasVoipUserAddon = (items?: { type?: { name?: string } }[]) =>
  items?.some(isVoipAddon) ?? false;

export const buildItemLabel = (item: any, plans: TAvailablePlan[]) => {
  const isDomainTransfer =
    isDomainItem(item) && item.type?.action === "transfer";
  const isDomainBackorder =
    isDomainItem(item) && item.type?.action === "backorder";

  if (isDomainTransfer) return "Domain Name Transfer";
  if (isDomainBackorder) return "Domain Name Backorder";

  const typeName = item?.type?.name;

  switch (typeName) {
    case "PaYG Call Credit":
      return "Call Credit";

    case "Number":
      return "Phone Number";

    case "User": {
      const plan = plans.find((p) => p.id === item.type.sub_type_id + 1);
      return `${plan?.name} Plan User`;
    }

    case "VoIP Plan Change":
      return `${item?.description?.split(" ").slice(0, 3).join(" ")} Plan`;

    case "VoIP Addon":
      if (item?.name === "Call Recording Retention") {
        return "Call Recording Add-On";
      }
      return "VoIP Add-On";

    default:
      return typeName || "Unknown Item";
  }
};
